import { useCallback, useEffect, useRef, useState } from "react";
import { billingSettingsApi, chargeCategoryApi, chargeItemApi } from "../api/api";
import {
  billingSettingsToApi, chargeItemToApi, mapBillingSettings, mapChargeCategory, mapChargeItem,
} from "../api/mappers";

const byOrder = (a, b) => a.order - b.order || a.id - b.id;

/* One guest house's billing setup: GST settings, bill headings and items.
   Every save answers with the server's copy, which replaces ours — the server
   normalises (GSTIN upper-cased, names trimmed) and that is what bills use.
   A response for a guest house the admin has since switched away from is
   dropped rather than shown under the wrong house. */
export default function useBillingCatalog(ghId) {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const currentGh = useRef(ghId);

  const load = useCallback(async () => {
    currentGh.current = ghId;
    if (!ghId) return;
    setLoading(true);
    setError("");
    try {
      const [s, cats, its] = await Promise.all([
        billingSettingsApi.get(ghId),
        chargeCategoryApi.list(ghId),
        chargeItemApi.list(ghId),
      ]);
      if (currentGh.current !== ghId) return;
      setSettings(mapBillingSettings(s));
      setCategories(cats.map(mapChargeCategory).sort(byOrder));
      setItems(its.map(mapChargeItem).sort(byOrder));
    } catch (e) {
      if (currentGh.current === ghId) setError(e.message || "Could not load billing settings.");
    } finally {
      if (currentGh.current === ghId) setLoading(false);
    }
  }, [ghId]);

  useEffect(() => {
    load();
    return () => { currentGh.current = null; };
  }, [load]);

  const saveSettings = async (form) => {
    const saved = mapBillingSettings(await billingSettingsApi.update(ghId, billingSettingsToApi(form)));
    if (currentGh.current === ghId) setSettings(saved);
    return saved;
  };

  const addCategory = async (name) => {
    const order = categories.length ? Math.max(...categories.map((c) => c.order)) + 1 : 0;
    const created = mapChargeCategory(await chargeCategoryApi.create(ghId, { name: name.trim(), order }));
    setCategories((prev) => [...prev, created].sort(byOrder));
    return created;
  };

  const renameCategory = async (id, name) => {
    const updated = mapChargeCategory(await chargeCategoryApi.update(ghId, id, { name: name.trim() }));
    setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
    // Items show their heading's name; refresh it without another request.
    setItems((prev) => prev.map((i) => (i.categoryId === id ? { ...i, categoryName: updated.name } : i)));
    return updated;
  };

  const deleteCategory = async (id) => {
    await chargeCategoryApi.delete(ghId, id);
    setCategories((prev) => prev.filter((c) => c.id !== id));
  };

  const saveItem = async (form, id = null) => {
    const body = chargeItemToApi(form);
    const saved = mapChargeItem(
      id ? await chargeItemApi.update(ghId, id, body) : await chargeItemApi.create(ghId, body),
    );
    setItems((prev) => (id ? prev.map((i) => (i.id === id ? saved : i)) : [...prev, saved]).sort(byOrder));
    return saved;
  };

  const setItemActive = async (id, isActive) => {
    const saved = mapChargeItem(await chargeItemApi.update(ghId, id, { is_active: isActive }));
    setItems((prev) => prev.map((i) => (i.id === id ? saved : i)));
    return saved;
  };

  const deleteItem = async (id) => {
    await chargeItemApi.delete(ghId, id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  return {
    settings, categories, items, loading, error, reload: load,
    saveSettings, addCategory, renameCategory, deleteCategory, saveItem, setItemActive, deleteItem,
  };
}
